<template>
  <div class="kanban-board">
    <!-- Toolbar -->
    <div class="kanban-board__toolbar">
      <div class="kanban-board__toolbar-left">
        <h2 class="kanban-board__title">
          <i class="i-lucide-kanban size-5 mr-2" />
          Kanban
        </h2>
        <span class="kanban-board__conv-count">
          {{ kanbanStore.totalCount }} conversas
        </span>
        <!-- Pipeline selector -->
        <select
          v-if="kanbanStore.pipelines.length"
          :value="kanbanStore.activePipelineId"
          class="kanban-board__filter-select reset-base"
          title="Pipeline"
          @change="onPipelineChange"
        >
          <option
            v-for="p in kanbanStore.pipelines"
            :key="p.id"
            :value="p.id"
          >
            {{ p.name }}
          </option>
        </select>
        <button
          class="kanban-board__btn"
          title="Novo pipeline"
          @click="onCreatePipeline"
        >
          <i class="i-lucide-plus size-4" />
        </button>
        <button
          v-if="kanbanStore.pipelines.length > 1"
          class="kanban-board__btn"
          title="Excluir pipeline atual"
          @click="onDeletePipeline"
        >
          <i class="i-lucide-trash-2 size-4" />
        </button>
      </div>
      <div class="kanban-board__toolbar-right">
        <!-- Search -->
        <div class="kanban-board__search">
          <i class="i-lucide-search kanban-board__search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar por nome, telefone..."
            class="kanban-board__search-input reset-base"
          />
        </div>

        <!-- Assignee filter -->
        <select v-model="assigneeFilter" class="kanban-board__filter-select reset-base">
          <option value="all">Todos</option>
          <option value="me">Atribuídos a mim</option>
          <option value="unattended">Sem resposta</option>
        </select>

        <!-- Inbox filter (mostra só inboxes com conversas presentes no kanban) -->
        <select v-model="inboxFilter" class="kanban-board__filter-select reset-base" title="Filtrar por canal/inbox">
          <option value="all">Todas inboxes</option>
          <option
            v-for="inb in availableInboxes"
            :key="inb.id"
            :value="inb.id"
          >
            {{ inb.name }}
          </option>
        </select>

        <!-- Reorder mode toggle -->
        <button
          class="kanban-board__btn"
          :class="{ 'kanban-board__btn--active': reorderMode }"
          :title="reorderMode ? 'Sair do modo reordenar' : 'Reordenar colunas'"
          @click="reorderMode = !reorderMode"
        >
          <i class="i-lucide-grip-vertical size-4" />
        </button>

        <!-- Add column -->
        <button
          class="kanban-board__btn"
          title="Nova coluna"
          @click="openNewColumnEditor"
        >
          <i class="i-lucide-columns-3 size-4" />
        </button>

        <!-- Export config -->
        <button
          class="kanban-board__btn"
          title="Exportar configuração"
          @click="kanbanStore.exportConfig()"
        >
          <i class="i-lucide-download size-4" />
        </button>

        <!-- Import config -->
        <label class="kanban-board__btn kanban-board__btn--label" title="Importar configuração">
          <i class="i-lucide-upload size-4" />
          <input
            type="file"
            accept="application/json"
            class="sr-only"
            @change="onImportConfig"
          />
        </label>

        <!-- Refresh -->
        <button
          class="kanban-board__btn"
          title="Recarregar conversas"
          @click="refresh"
        >
          <i
            class="i-lucide-refresh-cw size-4"
            :class="{ 'animate-spin': kanbanStore.isAnyLoading }"
          />
        </button>
      </div>
    </div>

    <!-- Board columns -->
    <div ref="columnsRef" class="kanban-board__columns" @wheel="onColumnsWheel">
      <KanbanColumn
        v-for="col in kanbanStore.columns"
        :key="col.id"
        :column="col"
        :cards="getFilteredCards(col.id)"
        :loading="kanbanStore.loadingByStatus[col.nativeStatus]"
        :reorder-mode="reorderMode"
        @card-click="openConversation"
        @card-moved="onCardMoved"
        @toggle-collapse="kanbanStore.toggleColumnCollapse($event)"
        @column-edit="openColumnEditor"
        @column-drag-start="onColumnDragStart"
        @column-drag-over="onColumnDragOver"
        @column-drop="onColumnDrop"
      />
    </div>

    <!-- KanbanEditor drawer -->
    <KanbanEditor
      v-model="editorOpen"
      :column="editingColumn"
      @save="onColumnSave"
      @delete="onColumnDelete"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { onMounted, onUnmounted } from 'vue';
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKanbanStore } from 'dashboard/stores/kanban';
import { useAccount } from 'dashboard/composables/useAccount';
import KanbanColumn from './KanbanColumn.vue';
import KanbanEditor from './KanbanEditor.vue';

const kanbanStore = useKanbanStore();
const vuexStore = useStore();
const { accountId } = useAccount();
const inboxesList = useMapGetter('inboxes/getInboxes');

const searchQuery = ref('');
const assigneeFilter = ref('all');
const inboxFilter = ref('all');

// Ref ao container das colunas — usado para converter scroll vertical do
// mouse em scroll horizontal do board quando o ponteiro não está sobre uma
// coluna rolável.
const columnsRef = ref(null);

function onColumnsWheel(e) {
  const el = columnsRef.value;
  if (!el) return;
  // Gestos horizontais (trackpad) seguem o comportamento padrão.
  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
  // Se o cursor está sobre o corpo de uma coluna que ainda pode rolar
  // verticalmente, mantemos o scroll vertical natural dentro da coluna.
  const body = e.target.closest && e.target.closest('.kanban-column__body');
  if (body && body.scrollHeight > body.clientHeight) {
    const atTop = body.scrollTop <= 0;
    const atBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1;
    const goingUp = e.deltaY < 0;
    const goingDown = e.deltaY > 0;
    if (!((atTop && goingUp) || (atBottom && goingDown))) return;
  }
  if (e.deltaY !== 0) {
    el.scrollLeft += e.deltaY;
    e.preventDefault();
  }
}

// Lista de inboxes que possuem ao menos uma conversa carregada no kanban
const availableInboxes = computed(() => {
  const ids = new Set();
  for (const status of Object.keys(kanbanStore.conversationsByStatus || {})) {
    for (const conv of kanbanStore.conversationsByStatus[status] || []) {
      if (conv.inbox_id) ids.add(conv.inbox_id);
    }
  }
  const all = inboxesList.value || [];
  return all
    .filter(i => ids.has(i.id))
    .map(i => ({ id: i.id, name: i.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

// Editor state
const editorOpen = ref(false);
const editingColumn = ref(null);

// Column reorder state
const reorderMode = ref(false);
const draggingColumnId = ref(null);

function onColumnDragStart(columnId) {
  draggingColumnId.value = columnId;
}

function onColumnDragOver(targetColumnId) {
  if (!draggingColumnId.value || draggingColumnId.value === targetColumnId) return;
  // Compute new order based on current column positions
  const cols = [...kanbanStore.columns];
  const fromIdx = cols.findIndex(c => c.id === draggingColumnId.value);
  const toIdx = cols.findIndex(c => c.id === targetColumnId);
  if (fromIdx === -1 || toIdx === -1) return;
  // Temporarily reorder for visual feedback (optimistic via store)
  const orderedIds = cols.map(c => c.id);
  orderedIds.splice(fromIdx, 1);
  orderedIds.splice(toIdx, 0, draggingColumnId.value);
  // Optimistic in-memory reorder for visual feedback; persisted on drop.
  kanbanStore.reorderColumnsLocal(orderedIds);
}

function onColumnDrop() {
  if (draggingColumnId.value) {
    const orderedIds = [...kanbanStore.columns].map(c => c.id);
    kanbanStore.reorderColumns(orderedIds);
  }
  draggingColumnId.value = null;
}

function onPipelineChange(event) {
  kanbanStore.setActivePipeline(Number(event.target.value));
}

async function onCreatePipeline() {
  // eslint-disable-next-line no-alert
  const name = window.prompt('Nome do novo pipeline:');
  if (!name || !name.trim()) return;
  await kanbanStore.createPipeline(name.trim());
}

async function onDeletePipeline() {
  const current = kanbanStore.activePipeline;
  if (!current) return;
  // eslint-disable-next-line no-alert
  const ok = window.confirm(
    `Excluir o pipeline "${current.name}" e todas as suas colunas? As conversas NÃO são afetadas.`
  );
  if (!ok) return;
  await kanbanStore.deletePipeline(current.id);
}

function openColumnEditor(column) {
  editingColumn.value = column;
  editorOpen.value = true;
}

function openNewColumnEditor() {
  editingColumn.value = null;
  editorOpen.value = true;
}

function onColumnSave(savedCol) {
  if (savedCol.id && kanbanStore.colsConfig.find(c => c.id === savedCol.id)) {
    kanbanStore.updateColumn(savedCol);
  } else {
    kanbanStore.addColumn(savedCol);
  }
}

function onColumnDelete(column) {
  if (!column?.id || column.system) return;
  kanbanStore.removeColumn(column.id);
}

async function onImportConfig(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  const text = await file.text();
  kanbanStore.importConfig(text);
  event.target.value = '';
}

function getFilteredCards(columnId) {
  let conversations = kanbanStore.getConversationsForColumn(columnId);
  // Filtro por inbox
  if (inboxFilter.value !== 'all') {
    const id = Number(inboxFilter.value);
    conversations = conversations.filter(c => c.inbox_id === id);
  }
  if (!searchQuery.value.trim()) return conversations;
  const q = searchQuery.value.toLowerCase();
  return conversations.filter(c => {
    const name = c.meta?.sender?.name?.toLowerCase() || '';
    const phone = c.meta?.sender?.phone_number?.toLowerCase() || '';
    const msg = c.last_non_activity_message?.content?.toLowerCase() || '';
    return name.includes(q) || phone.includes(q) || msg.includes(q);
  });
}

async function refresh() {
  await kanbanStore.fetchAllColumns(assigneeFilter.value);
}

function openConversation(conversation) {
  if (!conversation?.id) return;
  window.location.href = `/app/accounts/${accountId.value}/conversations/${conversation.id}`;
}

function onCardMoved({ conversationId, fromStatus, toColumnId }) {
  if (fromStatus === toColumnId) return;
  const conversations = kanbanStore.getConversationsForColumn(fromStatus);
  const conversation = conversations.find(c => c.id === conversationId);
  if (!conversation) return;
  kanbanStore.moveCard(conversation, fromStatus, toColumnId);
}

let unsubscribeVuex = null;

onMounted(async () => {
  await kanbanStore.loadPipelines();
  kanbanStore.fetchAllColumns(assigneeFilter.value);
  kanbanStore.startRulesEngine();

  // Sync Chatwoot → Kanban: when a conversation is updated via WebSocket
  // (status_changed, conversation_updated), move the card locally without
  // calling the API again.
  unsubscribeVuex = vuexStore.subscribe(mutation => {
    if (
      mutation.type === 'UPDATE_CONVERSATION' ||
      mutation.type === 'ADD_CONVERSATION'
    ) {
      kanbanStore.syncConversationFromBackend(mutation.payload);
    }
  });
});

onUnmounted(() => {
  kanbanStore.stopRulesEngine();
  if (unsubscribeVuex) {
    unsubscribeVuex();
    unsubscribeVuex = null;
  }
});
</script>

<style scoped>
.kanban-board {
  @apply flex flex-col flex-1 h-full w-full min-w-0 overflow-hidden bg-n-solid-2;
}
.kanban-board__toolbar {
  @apply flex flex-wrap items-center justify-between gap-y-2 px-3 sm:px-5 py-3 border-b border-n-weak
         bg-n-solid-2 flex-shrink-0;
}
.kanban-board__toolbar-left {
  @apply flex items-center gap-3 min-w-0;
}
.kanban-board__title {
  @apply flex items-center text-lg font-semibold text-n-slate-12;
}
.kanban-board__conv-count {
  @apply text-sm text-n-slate-9;
}
.kanban-board__toolbar-right {
  @apply flex flex-wrap items-center gap-2 min-w-0;
}
.kanban-board__search {
  @apply relative flex items-center flex-shrink-0;
}
.kanban-board__search-icon {
  @apply absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-9 pointer-events-none z-10;
}
.kanban-board__search-input {
  /* h-8 = 32px: mesma altura dos botões (size-8 / p-1.5 + size-4 ícone) */
  @apply h-8 pl-8 pr-3 text-sm bg-n-solid-1 border border-n-weak rounded-lg
         text-n-slate-12 placeholder:text-n-slate-9
         focus:outline-none focus:ring-1 focus:ring-n-iris-9 w-40 sm:w-52 flex-shrink-0;
}
.kanban-board__filter-select {
  /* w-36 (144px) garante espaço suficiente para a opção mais longa.
     py-0 sobrescreve o padding vertical do reset global de <select>.
     !m-0 anula o margin-bottom:1rem aplicado pelo .field-base global. */
  @apply h-8 w-36 px-2 py-0 !m-0 text-sm bg-n-solid-1 border border-n-weak rounded-lg
         text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-iris-9 flex-shrink-0;
}
.kanban-board__btn {
  /* size-8 padroniza todos os botões para 32x32px (input.h-8 + select.h-8) */
  @apply inline-flex items-center justify-center size-8 rounded-lg bg-n-solid-1
         border border-n-weak text-n-slate-9 flex-shrink-0
         hover:text-n-slate-12 hover:bg-n-solid-2 transition-colors;
}
.kanban-board__btn--label {
  @apply cursor-pointer;
}
.kanban-board__btn--active {
  @apply bg-n-iris-3 border-n-iris-7 text-n-iris-9;
}
.kanban-board__columns {
  @apply flex gap-3 sm:gap-4 p-3 sm:p-5 flex-1;
  overflow-x: scroll;
  overflow-y: hidden;
  /* Always reserve scrollbar space — prevents layout shift when columns collapse */
  scrollbar-gutter: stable both-edges;
  /* iOS smooth scrolling */
  -webkit-overflow-scrolling: touch;
}

/* Responsivo: telas pequenas (mobile/tablet) */
@media (max-width: 640px) {
  .kanban-board__title {
    @apply text-base;
  }
  .kanban-board__conv-count {
    @apply text-xs;
  }
  .kanban-board__filter-select {
    @apply w-32;
  }
}
</style>
